<template>
  <div>
    <slot name="header" />
    <div
      v-if="hasAnyItems || alwaysShowActions"
      class="mt-2 flex flex-wrap items-center gap-4 pb-2"
      :class="[showList ? 'justify-between' : 'justify-end']">
      <div v-if="showList" class="flex items-center space-x-2">
        <slot name="leftActions">
          <SelectButton v-model="displayGrid" :options="['Grid', 'List']" />
          <template v-if="false && showTags">
            <MultiSelect
              v-model="selectedTags"
              :options="availableTags"
              option-label="name"
              option-value="id"
              placeholder="Select category"
              display="chip" />
          </template>
        </slot>
      </div>
      <div class="flex flex-wrap items-center gap-2" :class="{ 'w-full': inlineFilters }">
        <slot name="actions" />
        <Button
          v-if="showFilters && !inlineFilters"
          label="Display"
          icon="pi pi-filter"
          severity="secondary"
          outlined
          :size="bigFilter ? undefined : 'small'"
          @click="displaySettings?.toggle($event)" />
        <Popover v-if="!inlineFilters" ref="displaySettings">
          <div class="space-y-3 text-sm">
            <div v-if="orderOptions.length > 0" class="flex items-center justify-between space-x-3">
              <div class="font-semibold"> Ordering </div>
              <div>
                <Select
                  v-model="orderValue"
                  :options="orderOptions"
                  option-label="label"
                  option-value="value"
                  class="w-[150px] text-xs" />
              </div>
            </div>
            <div v-if="false" class="flex items-center justify-between">
              <div class="font-semibold"> Filtering </div>
              <div>
                <MultiSelect class="w-[150px] text-xs" placeholder="Alphabetical" />
              </div>
            </div>
            <slot name="filters" />
          </div>
        </Popover>
        <template v-if="showSearch">
          <InputText
            v-if="!bigFilter"
            v-model="filterQuery"
            :placeholder="searchPlaceholder ?? 'Filter'"
            size="small" />
          <InputText
            v-else
            v-model="filterQuery"
            :placeholder="searchPlaceholder ?? 'Search'"
            class="w-full"
            size="large" />
        </template>
        <slot v-if="showFilters && inlineFilters" name="filters" />
      </div>
    </div>

    <div class="mt-2">
      <template v-if="hasDisplayItems">
        <div v-if="displayGrid === 'Grid' && grouped" class="space-y-8">
          <div v-for="group in groupedDisplayItems" :key="group.header" class="space-y-4">
            <h3 class="text-lg font-normal text-gray-900 opacity-75 dark:text-gray-100">
              {{ group.header }}
            </h3>
            <TransitionGroup name="grid" tag="div" class="grid grid-cols-1 gap-8" :class="gridOptions">
              <slot v-for="item in group.items" v-bind="{ item }" :key="itemKey(item)" name="grid-item" />
            </TransitionGroup>
          </div>
        </div>
        <TransitionGroup
          v-else-if="displayGrid === 'Grid'"
          name="grid"
          tag="div"
          class="grid grid-cols-1 gap-8"
          :class="gridOptions">
          <slot v-for="item in displayItems" v-bind="{ item }" :key="itemKey(item)" name="grid-item" />
        </TransitionGroup>
        <DataTable
          v-else-if="displayGrid === 'List'"
          :value="displayItems"
          selection-mode="single"
          @row-select="emits('row-select', $event)">
          <Column v-for="col of columns" :key="col.field" :field="col.field" :header="col.header" :sortable="true">
            <template v-if="$slots[`column-${col.field}`]" #body="slotProps">
              <slot :name="`column-${col.field}`" v-bind="{ data: slotProps.data, field: slotProps.field }" />
            </template>
          </Column>
          <Column v-if="canDelete" :exportable="false">
            <template #body="slotProps">
              <div class="flex items-center justify-end">
                <Button
                  icon="pi pi-trash"
                  outlined
                  size="small"
                  severity="danger"
                  @click="emits('row-delete', slotProps.data)" />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
      <div v-else>
        <template v-if="isFiltering && hasAnyItems">
          <!-- Show message when it's just a filter with no results -->
          <div class="py-8 text-center text-gray-500"> No items match your search </div>
        </template>
        <template v-else>
          <slot name="empty-slate" />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends object">
import { useStorage } from '@vueuse/core'
import type { SortConfig, SortFieldType, SortDirection } from '@dashboard/types/sorting'
import { useTagStore } from '@dashboard/stores/tagStore'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import InputText from 'primevue/inputtext'
import MultiSelect from 'primevue/multiselect'
import Popover from 'primevue/popover'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import { computed, ref, watch, watchEffect } from 'vue'
import type { PropType } from 'vue'

type GridItem = T
type GridGroup = { header: string; items: GridItem[] }
type GridColumn = { field: string; header: string }

const isGridGroup = (item: GridItem | GridGroup): item is GridGroup =>
  'header' in item && typeof item.header === 'string' && 'items' in item && Array.isArray(item.items)

const props = defineProps({
  name: { type: String, required: true },
  items: { type: Array as PropType<GridItem[] | GridGroup[]>, required: true },
  showTags: { type: Boolean, default: false },
  columns: { type: Array as PropType<GridColumn[]>, required: true },
  columnCount: { type: Number, default: 3 },
  showList: { type: Boolean, default: true },
  showFilters: { type: Boolean, default: false },
  showActions: { type: Boolean, default: true },
  showSearch: { type: Boolean, default: true },
  inlineFilters: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: null },
  bigFilter: { type: Boolean, default: false },
  id: { type: String, default: 'id' },
  canDelete: { type: Boolean, default: false },
  grouped: { type: Boolean, default: false },
  pinField: { type: String, default: null },
  sortConfig: {
    type: Object as PropType<SortConfig>,
    default: null
  },
  alwaysShowActions: { type: Boolean, default: false },
  totalItems: { type: Number, default: null }
})

const emits = defineEmits(['filter', 'row-select', 'row-delete'])

const displayGrid = useStorage(`${props.name}-display`, 'Grid')
const defaultSortField = computed(
  () => props.sortConfig?.defaultField || props.sortConfig?.fields?.[0]?.value || 'name'
)
const orderValue = useStorage(`${props.name}-order`, defaultSortField.value)
const selectedTags = useStorage(`${props.name}-tags`, [])
const filterQuery = ref('')
const displaySettings = ref<InstanceType<typeof Popover> | null>(null)

// Safety check: Reset to Grid if stuck in Archived view (legacy cleanup)
watchEffect(() => {
  if (displayGrid.value === 'Archived') {
    displayGrid.value = 'Grid'
  }
})

watch(filterQuery, (newVal) => {
  emits('filter', newVal)
})

const itemKey = (item: GridItem): string | number => {
  const key: unknown = Reflect.get(item, props.id)
  return typeof key === 'string' || typeof key === 'number' ? key : ''
}

const groupedItems = computed<GridGroup[]>(() =>
  props.items.filter(isGridGroup).map(({ header, items }) => ({ header, items }))
)

const isFiltering = computed(() => filterQuery.value.length > 0)

// Track if we have any items (either from totalItems prop or current items)
const hasAnyItems = computed(() => {
  if (isFiltering.value) {
    return true
  }

  // If totalItems is provided, use that to know if there are items before filtering
  if (props.totalItems !== null) {
    return props.totalItems > 0
  }
  // Otherwise fall back to checking current items
  if (props.grouped) {
    return groupedItems.value.some((group) => group.items.length > 0)
  }
  return props.items.length > 0
})

// Type-aware comparison function
const compareByType = (a: any, b: any, type: SortFieldType, direction: SortDirection): number => {
  const multiplier = direction === 'asc' ? 1 : -1

  switch (type) {
    case 'string':
      return (
        String(a ?? '')
          .toLowerCase()
          .localeCompare(String(b ?? '').toLowerCase()) * multiplier
      )
    case 'number':
      return ((Number(a) || 0) - (Number(b) || 0)) * multiplier
    case 'datetime':
      return ((a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0)) * multiplier
    case 'boolean':
      return ((a ? 1 : 0) - (b ? 1 : 0)) * multiplier
    default:
      return 0
  }
}

// Get value from item using field config
const getFieldValue = (item: any, fieldConfig: any): any => {
  if (fieldConfig.accessor) {
    return fieldConfig.accessor(item)
  }
  return item[fieldConfig.value]
}

const groupedDisplayItems = computed<GridGroup[]>(() => groupedItems.value.filter((group) => group.items.length > 0))

const displayItems = computed<GridItem[]>(() => {
  if (props.grouped) {
    return []
  }

  const items = props.items as GridItem[]

  // Handle regular mode - apply sorting if sortConfig is provided
  if (!props.showFilters || !props.sortConfig) {
    return items
  }

  const fieldConfig = props.sortConfig.fields.find((f) => f.value === orderValue.value)
  if (!fieldConfig) {
    return items
  }

  const sortFn = (a: any, b: any) => {
    const aVal = getFieldValue(a, fieldConfig)
    const bVal = getFieldValue(b, fieldConfig)
    return compareByType(aVal, bVal, fieldConfig.type, fieldConfig.direction)
  }

  // If pinField is set, partition into pinned (truthy) and unpinned, sort each group independently
  if (props.pinField) {
    const pinned = items.filter((item) => Reflect.get(item, props.pinField!)).sort(sortFn)
    const unpinned = items.filter((item) => !Reflect.get(item, props.pinField!)).sort(sortFn)
    return [...pinned, ...unpinned]
  }

  return [...items].sort(sortFn)
})

const hasDisplayItems = computed(() =>
  props.grouped ? groupedDisplayItems.value.length > 0 : displayItems.value.length > 0
)

const gridOptions = computed(() => {
  if (props.columnCount === 3) {
    return 'sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3'
  } else if (props.columnCount === 2) {
    return 'sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-2'
  }
  return ''
})

const orderOptions = computed(() => {
  if (!props.sortConfig?.fields) {
    return []
  }
  return props.sortConfig.fields.map((f) => ({
    label: f.label,
    value: f.value
  }))
})

const tagStore = useTagStore()
const { tags: availableTags } = storeToRefs(tagStore)

/*

slots:
- empty slate
- grid item
- list item
- actions

*/
</script>
